
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, CreditCard, CheckCircle, AlertTriangle, Trophy } from "lucide-react";

interface GolfRegistrationData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  golf_index: number;
  amount: number;
  payment_status: string;
}

export default function GolfPaiement() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const registrationId = searchParams.get("id");

  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [registrationData, setRegistrationData] = useState<GolfRegistrationData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!registrationId) {
        setError("Identifiant d'inscription manquant");
        setIsLoading(false);
        return;
      }

      try {
        const { data, error: rpcError } = await supabase
          .rpc("get_golf_registration_for_payment", { registration_id: registrationId } as any);

        if (rpcError) throw rpcError;

        const results = data as any[];
        if (!results || results.length === 0) {
          throw new Error("Inscription non trouvée ou déjà payée");
        }

        setRegistrationData(results[0] as GolfRegistrationData);
      } catch (err: any) {
        setError(err.message || "Une erreur est survenue");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [registrationId]);

  const handlePayment = async () => {
    if (!registrationData) return;
    setIsPaymentProcessing(true);

    try {
      const result = await supabase.functions.invoke("create-golf-payment", {
        body: {
          registrationId: registrationData.id,
          amount: registrationData.amount,
          email: registrationData.email,
          firstName: registrationData.first_name,
          lastName: registrationData.last_name,
        },
      });

      if (result.error) throw result.error;
      const data = result.data as any;

      if (data?.sessionUrl) {
        if (data.sessionId) {
          localStorage.setItem("golf_stripe_session_id", data.sessionId);
        }
        window.location.href = data.sessionUrl;
      } else {
        throw new Error("Impossible de créer la session de paiement");
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      toast({
        variant: "destructive",
        title: "Erreur de paiement",
        description: "Impossible de procéder au paiement. Veuillez réessayer.",
      });
      setIsPaymentProcessing(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Background with gradient and gavroche */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-gradient-to-br from-[#FFFF4D] via-[#FFE94B] to-[#A3D960]"></div>
        <div className="absolute top-0 right-0 h-full w-1/3 md:w-2/5 opacity-30 md:opacity-40 flex items-end">
          <img 
            src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" 
            alt="Décoration" 
            className="h-auto w-full object-contain object-bottom drop-shadow-xl" 
          />
        </div>
      </div>
      <div className="max-w-xl w-full mx-4 relative z-10">
        <div className="text-center mb-6">
          <Trophy className="h-10 w-10 text-[#00509E] mx-auto mb-2" />
          <h1 className="text-xl font-bold text-[#001233]">Golf 2026</h1>
        </div>
        <Card className="border-0 shadow-2xl">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-8">
                <Loader2 className="h-10 w-10 text-[#00509E] animate-spin mx-auto" />
                <p className="mt-2 text-muted-foreground">Chargement...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-2" />
                <h2 className="text-xl font-bold text-red-500 mb-2">Erreur</h2>
                <p className="text-muted-foreground">{error}</p>
                <Button className="mt-4" onClick={() => navigate("/inscription-golf2026")}>
                  Retour à l'inscription
                </Button>
              </div>
            ) : registrationData && (
              <>
                <div className="text-center mb-6">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <h2 className="text-2xl font-bold text-foreground">Finaliser votre inscription</h2>
                  <p className="text-muted-foreground">Procédez au paiement pour confirmer votre participation</p>
                </div>
                <div className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">Nom</span>
                      <span>{registrationData.first_name} {registrationData.last_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Email</span>
                      <span>{registrationData.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Indice de golf</span>
                      <span>{registrationData.golf_index}</span>
                    </div>
                  </div>
                  <Separator />
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>{registrationData.amount} €</span>
                    </div>
                  </div>
                  <Button
                    onClick={handlePayment}
                    disabled={isPaymentProcessing}
                    className="w-full py-6 bg-[#00509E] hover:bg-[#003d7a] text-white text-lg"
                  >
                    {isPaymentProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Traitement...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Payer {registrationData.amount} €
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
